import type { SupWeb3Address } from "@/types";
import type { BigNumberish } from "@ethersproject/bignumber";
import { useMemo } from "react";

import useApi from "@/hooks/api/useApi";
import useWeb3UserAddress from "@/hooks/web3/useWeb3UserAddress";

export interface PiggyDepositStartResponse {
  transactionData: SupWeb3Address;
  to: SupWeb3Address;
  value: BigNumberish;
  approval?: {
    transactionData: SupWeb3Address;
    to: SupWeb3Address;
    approvingAmount: SupWeb3Address;
  };
  gasPriceInGwei: number;
}

/**
 * Hook to start deposit operation
 * @package api:go
 * @link https://superform-xyz.postman.co/workspace/Superform~fe57d318-f198-4f97-9c50-0b2a5a78dbac/request/30355904-e6c2fcf7-3089-462f-8173-9df81d8c8c39?action=share&source=copy-link&creator=26633220
 */
export default function usePiggyDepositStart(amount: bigint, approvalRequired: boolean = false) {
  const userAddress = useWeb3UserAddress();

  const { path, params } = useMemo(
    () => ({
      path: `/token-distribution/slop-bucket/start/deposit/${userAddress}/`,
      params: {
        amount: amount.toString(),
        approval_required: approvalRequired,
      },
    }),
    [amount, approvalRequired, userAddress]
  );

  const { data, isLoading, isError, error, mutate } = useApi<PiggyDepositStartResponse, PiggyDepositStartResponse>(
    {
      path,
      params,
      api: "v2",
    },
    {
      enabled: !!userAddress && amount !== 0n,
      strategy: "fully-cached",
    }
  );

  return {
    data,
    isLoading,
    isError,
    error,
    mutate,
  };
}
